import { useState, useEffect } from 'react';
import { message } from 'antd';
import { adminService } from '../../api/services/adminService';

export interface Listing {
  id: string;
  title: string;
  make: string;
  model: string;
  year: number;
  price: string | number;
  user: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
  paymentToken: string | null;
  paymentAmount: string | null;
  paymentDate: string | null;
  paymentReference: string | null;
  submittedAt: string | null;
  status: string;
  verificationInfo: any;
}

export interface ListingsResponse {
  listings: Listing[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export const useAllListings = () => {
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 5,
    pages: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    q: '', // ✅ replaced 'search' with 'q'
    status: '',
    dateRange: null as any,
  });

  const fetchListings = async (page = 1, limit = 5) => {
    setLoading(true);
    try {
      const response = await adminService.searchListings(
        filters.q || '',
        filters,
        page,
        limit
      );
      const apiData = response as any;

      setListings(apiData.listings || []);
      setPagination(
        apiData.pagination || { total: 0, page: 1, limit: 5, pages: 0 }
      );
    } catch (error) {
      message.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminService.deleteListing(id);
      message.success('Listing deleted successfully');
      fetchListings(pagination.page, pagination.limit);
      setDeleteModalVisible(false);
    } catch (error) {
      console.error('Failed to delete listing:', error);
      message.error('Failed to delete listing');
    }
  };

  const handleBulkDelete = async () => {
    const ids = selectedRowKeys as string[];
    const results = await Promise.allSettled(
      ids.map(id => adminService.deleteListing(id))
    );
    const failed = results.filter(r => r.status === 'rejected').length;
    const succeeded = results.filter(r => r.status === 'fulfilled').length;

    if (failed === 0) {
      message.success(`${succeeded} listings deleted successfully`);
    } else if (succeeded === 0) {
      message.error(`Failed to delete all ${failed} listings`);
    } else {
      message.warning(
        `${succeeded} deleted, ${failed} failed to delete`
      );
    }

    setSelectedRowKeys([]);
    fetchListings(pagination.page, pagination.limit);
  };

  const handleViewDetails = async (id: string) => {
    try {
      const response = await adminService.getListingDetails(id);
      setSelectedListing(response);
      setDetailsModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch listing details:', error);
      message.error('Failed to load listing details');
    }
  };

  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleTableChange = (pagination: any) => {
    fetchListings(pagination.current, pagination.pageSize);
  };

  useEffect(() => {
    fetchListings();
  }, [filters]);

  return {
    loading,
    listings,
    pagination,
    selectedRowKeys,
    selectedListing,
    detailsModalVisible,
    deleteModalVisible,
    filters,
    setSelectedRowKeys,
    setDetailsModalVisible,
    setDeleteModalVisible,
    handleDelete,
    handleBulkDelete,
    handleViewDetails,
    handleFilterChange,
    handleTableChange,
    refetch: () => fetchListings(pagination.page, pagination.limit),
  };
};
